<template>
  <div>
    <div class="content mt-6 p-4 md:p-0" v-if="!vault.storage_exceeded">
      <!-- Drop zone: one line of copy, highlighted while a file is dragged over it -->
      <div
        class="drop-target border-2 border-dashed rounded w-full py-6 md:py-0 md:h-40 px-6 flex items-center justify-center text-center"
        :class="dragging ? 'bg-brand-lighter border-brand' : 'bg-brand-lightest border-brand-light'"
        ref="drop_target"
        @dragenter="dragDepth++; dragging = true"
        @dragleave="dragDepth--; dragging = dragDepth > 0"
        @drop="dragDepth = 0; dragging = false"
      >
        <div class="flex flex-col md:flex-row justify-center items-center">
          <img
            :src="'/images/' + vault_type + '-icn_upload.png'"
            alt=""
            class="h-12 mb-3 md:mb-0 md:mr-4"
          />
          <p class="text-lg md:text-2xl font-light flex flex-col md:flex-row items-center">
            <mq-layout mq="sm_desktop+">
              <span class="mr-2">Drag and drop files here, or</span>
            </mq-layout>
            <mq-layout :mq="['mobile', 'tablet']">
              <span class="mb-3">Upload from your drive or device</span>
            </mq-layout>
            <!-- plupload uses this button as the browse target -->
            <button
              type="button"
              class="ml-1 border border-brand bg-white text-brand px-3 py-1 rounded text-sm font-medium hover:bg-brand hover:text-white"
              ref="browse_files"
            >
              Select files
            </button>
          </p>
        </div>
      </div>

      <!-- Uploads: only once something has been added; name left, status right, thin bar under -->
      <div class="result mt-6 border rounded" v-if="uploads.length > 0">
        <div class="header border-b px-4 py-3 flex justify-between items-center">
          <span class="font-medium">Uploaded files</span>
          <router-link
            v-if="doneCount > 0"
            :to="{ name: 'tagging', params: { vault_id: vault_id } }"
            class="text-sm text-brand font-medium no-underline hover:underline"
          >
            File and tag {{ doneCount }} {{ doneCount == 1 ? "document" : "documents" }}
            <i class="fa fa-arrow-right ml-1"></i>
          </router-link>
        </div>
        <div class="px-4 py-1">
          <div
            v-for="file in uploads"
            :key="file.id"
            class="py-2 border-b border-grey-lighter"
          >
            <div class="flex justify-between items-center text-sm">
              <span class="font-medium truncate pr-4" :title="file.name">{{ file.name }}</span>
              <span class="flex-none" :class="statusClass(file)">
                <i class="fa mr-1" :class="statusIcon(file)"></i>{{ statusText(file) }}
              </span>
            </div>
            <div class="mt-2 h-1 rounded-full bg-grey-lighter overflow-hidden">
              <div
                class="h-full rounded-full"
                :class="file.failed ? 'bg-red' : 'bg-brand'"
                :style="{ width: (file.failed ? 100 : file.percent) + '%' }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="content mt-6 p-4 md:p-0" v-else>
      <div
        class="drop-target bg-red-lightest border-2 border-dashed border-red rounded w-full p-6 text-center"
      >
        Vault Storage Exceeded, uploading is not allowed
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { formatUploadError } from "../../upload_error.mjs";

export default {
  components: { mapGetters },

  mounted() {
    if (!this.vault.storage_exceeded) {
      this.initializeUploader();
    }
  },

  data() {
    return {
      vault_id: this.$route.params.vault_id,
      show_errors: false,
      pluploader_object: null,
      // Reactive mirror of the plupload queue; plupload's own objects are not observed by Vue
      uploads: [],
      dragging: false,
      dragDepth: 0,
    };
  },

  methods: {
    startUpload() {
      this.pluploader_object.start();
    },
    removeFile(file) {
      let status_before = file.status;
      this.pluploader_object.removeFile(file);
      if (
        this.pluploader_object.state == plupload.STARTED &&
        status_before == plupload.UPLOADING
      ) {
        this.pluploader_object.stop();
        this.pluploader_object.start();
      }
    },
    initializeUploader() {
      this.pluploader_object = new plupload.Uploader({
        browse_button: this.$refs.browse_files,
        drop_element: this.$refs.drop_target,
        url: `/api/vaults/${this.vault_id}/files/upload`,
        chunk_size: "50mb",
        multi_selection: true,
        multipart_params: {
          _token: Laravel.csrfToken,
        },
      });

      this.pluploader_object.bind("FilesAdded", (up, files) => {
        files.forEach((file) => {
          this.uploads.push({ id: file.id, name: file.name, percent: 0, done: false, failed: false });
        });
        this.startUpload();
      });

      this.pluploader_object.bind("UploadProgress", (up, file) => {
        this.trackUpload(file.id, { percent: file.percent });
      });

      this.pluploader_object.bind("FileUploaded", (up, file) => {
        this.trackUpload(file.id, { percent: 100, done: true });
      });

      this.pluploader_object.bind("Error", (up, error) => {
        if (error.file) {
          this.trackUpload(error.file.id, { failed: true });
        }
        this.$noty.error(formatUploadError(error));
      });

      this.pluploader_object.bind("UploadComplete", (up, files) => {
        _.delay(() => {
          this.$ebus.$emit("update-unfiled");
          this.$ebus.$emit("refresh-vaults", { vaultId: this.vault_id});
        }, 2000);
      });

      this.pluploader_object.init();
    },
    selectVault(vault) {
      this.vault_id = vault.value;
    },

    trackUpload(id, changes) {
      const entry = this.uploads.find((upload) => upload.id === id);
      if (entry) {
        Object.assign(entry, changes);
      }
    },

    statusText(file) {
      if (file.failed) return "Failed";
      if (file.done) return "Uploaded";
      return file.percent + "%";
    },

    statusClass(file) {
      if (file.failed) return "text-red";
      if (file.done) return "text-green";
      return "text-grey-darker";
    },

    statusIcon(file) {
      if (file.failed) return "fa-times-circle";
      if (file.done) return "fa-check-circle";
      return "fa-spin fa-circle-notch";
    },
  },

  computed: {
    ...mapGetters({
      allVaults: "getVaults",
      vault: "getCurrentVault",
    }),

    vault_type() {
      return window.vault_type;
    },

    doneCount() {
      return this.uploads.filter((upload) => upload.done).length;
    },
  },
};
</script>

<style>
</style>
